import { supabase } from './supabase';
import type { Database } from './database.types';

// Database notification type
type DbNotification = Database['public']['Tables']['notifications']['Row'];

// Notification type definitions
export type NotificationType = 
  | 'analysis_completed'
  | 'team_invite' 
  | 'project_shared'
  | 'credit_low'
  | 'subscription_update'
  | 'team_member_joined'
  | 'version_created';

// Type guard to check if a string is a valid NotificationType
function isNotificationType(type: string): type is NotificationType {
  return [
    'analysis_completed',
    'team_invite',
    'project_shared',
    'credit_low',
    'subscription_update',
    'team_member_joined',
    'version_created'
  ].includes(type);
}

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  read_at: string | null;
  action_url: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationRequest {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
}

export interface NotificationWithIcon extends Notification {
  icon: string;
  iconColor: string;
  timeAgo: string;
}

// Convert database notification to our Notification interface
function convertDbNotification(dbNotification: DbNotification): Notification | null {
  // Validate required fields
  if (!dbNotification.id || !dbNotification.user_id || !dbNotification.type || 
      !dbNotification.title || !dbNotification.message) {
    console.error('Invalid notification data:', dbNotification);
    return null;
  }

  // Check if type is valid
  if (!isNotificationType(dbNotification.type)) {
    console.error('Invalid notification type:', dbNotification.type);
    return null;
  }

  return {
    id: dbNotification.id,
    user_id: dbNotification.user_id,
    type: dbNotification.type,
    title: dbNotification.title,
    message: dbNotification.message,
    read: dbNotification.read ?? false,
    read_at: dbNotification.read_at,
    action_url: dbNotification.action_url,
    metadata: (dbNotification.metadata || {}) as Record<string, any>,
    created_at: dbNotification.created_at || new Date().toISOString(),
    updated_at: dbNotification.updated_at || new Date().toISOString()
  };
}

export class NotificationService {
  
  /**
   * Create a new notification
   */
  static async createNotification(request: CreateNotificationRequest): Promise<Notification | null> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: request.userId,
          type: request.type,
          title: request.title,
          message: request.message,
          action_url: request.actionUrl || null,
          metadata: request.metadata || {}
        })
        .select()
        .single();

      if (error) {
        console.error('Failed to create notification:', error);
        return null;
      }

      return convertDbNotification(data);
    } catch (error) {
      console.error('Create notification error:', error);
      return null;
    }
  }

  /**
   * Get notifications for the current user
   */
  static async getUserNotifications(
    limit: number = 20,
    offset: number = 0,
    unreadOnly: boolean = false
  ): Promise<NotificationWithIcon[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (unreadOnly) {
        query = query.eq('read', false);
      }

      const { data: notifications, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch notifications: ${error.message}`);
      }

      const results: NotificationWithIcon[] = [];
      for (const dbNotification of notifications || []) {
        const notification = convertDbNotification(dbNotification);
        if (notification) {
          results.push({
            ...notification,
            icon: this.getNotificationIcon(notification.type),
            iconColor: this.getNotificationColor(notification.type),
            timeAgo: this.formatTimeAgo(notification.created_at)
          });
        }
      }
      return results;
    } catch (error) {
      console.error('Get user notifications error:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   */
  static async getUnreadCount(): Promise<number> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return 0;
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Failed to get unread count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Get unread count error:', error);
      return 0;
    }
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true, 
          read_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) {
        console.error('Failed to mark notification as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Mark as read error:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read for current user
   */
  static async markAllAsRead(): Promise<boolean> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return false;
      }

      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true, 
          read_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Failed to mark all as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Mark all as read error:', error);
      return false;
    }
  }

  /**
   * Delete old read notifications (cleanup)
   */
  static async cleanupOldNotifications(daysOld: number = 30): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('read', true)
        .lt('read_at', cutoffDate.toISOString());

      if (error) {
        console.error('Failed to cleanup old notifications:', error);
      }
    } catch (error) {
      console.error('Cleanup old notifications error:', error);
    }
  }

  /**
   * Subscribe to real-time notifications for current user
   */
  static subscribeToNotifications(
    callback: (notification: NotificationWithIcon) => void
  ): (() => void) | null {
    try {
      const channel = supabase
        .channel('notifications')
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'notifications' 
          }, 
          (payload) => {
            const dbNotification = payload.new as DbNotification;
            const notification = convertDbNotification(dbNotification);
            if (notification) {
              callback({
                ...notification,
                icon: this.getNotificationIcon(notification.type),
                iconColor: this.getNotificationColor(notification.type),
                timeAgo: this.formatTimeAgo(notification.created_at)
              });
            }
          }
        )
        .subscribe();

      return () => {
        channel.unsubscribe();
      };
    } catch (error) {
      console.error('Subscribe to notifications error:', error);
      return null;
    }
  }

  /**
   * Helper methods for notification display
   */
  private static getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case 'analysis_completed':
        return 'BarChart3';
      case 'team_invite':
        return 'UserPlus';
      case 'project_shared':
        return 'Share';
      case 'credit_low':
        return 'AlertTriangle';
      case 'subscription_update':
        return 'CreditCard';
      case 'team_member_joined':
        return 'Users';
      case 'version_created':
        return 'GitBranch';
      default:
        return 'Bell';
    }
  }

  private static getNotificationColor(type: NotificationType): string {
    switch (type) {
      case 'analysis_completed':
        return 'text-blue-500';
      case 'team_invite':
      case 'team_member_joined':
        return 'text-green-500';
      case 'project_shared':
        return 'text-purple-500';
      case 'credit_low':
        return 'text-orange-500';
      case 'subscription_update':
        return 'text-indigo-500';
      case 'version_created':
        return 'text-cyan-500';
      default:
        return 'text-gray-500';
    }
  }

  private static formatTimeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Convenience methods for creating specific notification types
   */

  static async createAnalysisCompletedNotification(
    userId: string,
    creativeName: string,
    overallScore?: number,
    creativeId?: string
  ): Promise<void> {
    const scoreText = overallScore ? ` with a score of ${overallScore}%` : '';
    await this.createNotification({
      userId,
      type: 'analysis_completed',
      title: 'Analysis Complete',
      message: `Analysis completed for "${creativeName}"${scoreText}`,
      actionUrl: creativeId ? `/dashboard/creatives/${creativeId}` : undefined,
      metadata: { creativeName, overallScore, creativeId }
    });
  }

  static async createTeamInviteNotification(
    userId: string,
    inviterName: string,
    projectName: string,
    projectId: string
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'team_invite',
      title: 'Team Invitation',
      message: `${inviterName} invited you to join "${projectName}"`,
      actionUrl: `/dashboard/projects/${projectId}`,
      metadata: { inviterName, projectName, projectId }
    });
  }

  static async createTeamMemberJoinedNotification(
    userId: string,
    memberName: string,
    projectName: string,
    projectId: string
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'team_member_joined',
      title: 'New Team Member',
      message: `${memberName} joined your project "${projectName}"`,
      actionUrl: `/dashboard/projects/${projectId}`,
      metadata: { memberName, projectName, projectId }
    });
  }

  static async createProjectSharedNotification(
    userId: string,
    sharerName: string,
    projectName: string,
    projectId: string
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'project_shared',
      title: 'Project Shared',
      message: `${sharerName} shared "${projectName}" with you`,
      actionUrl: `/dashboard/projects/${projectId}`,
      metadata: { sharerName, projectName, projectId }
    });
  }

  static async createCreditLowNotification(
    userId: string,
    remainingCredits: number
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'credit_low',
      title: 'Credits Running Low',
      message: `You have only ${remainingCredits} credits remaining`,
      actionUrl: '/dashboard/credits',
      metadata: { remainingCredits }
    });
  }

  static async createSubscriptionUpdateNotification(
    userId: string,
    action: 'upgraded' | 'downgraded' | 'cancelled' | 'renewed',
    planName: string
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'subscription_update',
      title: 'Subscription Updated',
      message: `Your subscription has been ${action}${planName ? ` to ${planName}` : ''}`,
      actionUrl: '/dashboard/settings?tab=billing',
      metadata: { action, planName }
    });
  }

  static async createVersionCreatedNotification(
    userId: string,
    creativeName: string,
    versionNumber: number,
    creativeId: string
  ): Promise<void> {
    await this.createNotification({
      userId,
      type: 'version_created',
      title: 'New Version Created',
      message: `Version ${versionNumber} of "${creativeName}" has been created`,
      actionUrl: `/dashboard/creatives/${creativeId}`,
      metadata: { creativeName, versionNumber, creativeId }
    });
  }
}

export default NotificationService;